import { useQuery } from "@tanstack/react-query";
import { getConfig } from "@Api/estudo";
import { keycloakService } from "@Services/keycloak";
import { keycloakConfig } from "@Config/keycloak";
import { clientRolesFromToken } from "@Utils/jwt";

/**
 * Estado de acesso do usuário aos DOIS fluxos da ferramenta interna, derivado das
 * roles do Keycloak no access token + o estado do gate vindo do GET /config.
 *
 * - `canBase`    → pode "Avaliar clientes atuais" (dados da base RF).
 * - `canExterno` → pode enviar planilha / "Nova transportadora" (dados externos).
 *
 * Gate DESLIGADO (default até o SSO ter as roles) = comportamento legado: libera os
 * dois fluxos. Isto é apenas UX; o backend é a fronteira real (403 por rota).
 */
export interface Acesso {
  isLoading: boolean;
  gateEnabled: boolean;
  canBase: boolean;
  canExterno: boolean;
  hasAny: boolean;
  // Escopo por autor: true = vê estudos de todos; false = só os próprios. Gate OFF = true.
  verTodos: boolean;
  // Acesso à área de Relatórios. Gate OFF = true.
  canRelatorios: boolean;
  // Admin interno. Gate OFF = true.
  isAdmin: boolean;
  // Acesso à área de Inteligência de Preços (admin OU papel `precos`). Gate OFF = true.
  canPrecos: boolean;
}

const ROLE_BASE_DEFAULT = "analise_dados_base";
const ROLE_EXTERNOS_DEFAULT = "analise_dados_externos";
const ROLE_VER_TODOS_DEFAULT = "analise_ver_todos";
const ROLE_RELATORIOS_DEFAULT = "relatorios";
const ROLE_ADMIN_DEFAULT = "analise_admin";
const ROLE_PRECOS_DEFAULT = "precos";

export const useAcesso = (): Acesso => {
  // Mesma queryKey do rodapé da sidebar → compartilha o cache do GET /config.
  const { data, isLoading } = useQuery({
    queryKey: ["estudo-config"],
    queryFn: getConfig,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

  const gateEnabled = data?.acesso?.gateEnabled ?? false;
  const roleBase = data?.acesso?.roles?.base ?? ROLE_BASE_DEFAULT;
  const roleExterno = data?.acesso?.roles?.externos ?? ROLE_EXTERNOS_DEFAULT;
  const roleVerTodos = data?.acesso?.roles?.verTodos ?? ROLE_VER_TODOS_DEFAULT;
  const roleRelatorios = data?.acesso?.roles?.relatorios ?? ROLE_RELATORIOS_DEFAULT;

  const roles = clientRolesFromToken(
    keycloakService.getAccessToken(),
    keycloakConfig.clientId
  );

  // Gate OFF → libera tudo (legado). Gate ON → exige a role.
  const canBase = !gateEnabled || roles.includes(roleBase);
  const canExterno = !gateEnabled || roles.includes(roleExterno);
  const verTodos = !gateEnabled || roles.includes(roleVerTodos);
  const canRelatorios = !gateEnabled || roles.includes(roleRelatorios);
  const roleAdmin = data?.acesso?.roles?.admin ?? ROLE_ADMIN_DEFAULT;
  const rolePrecos = data?.acesso?.roles?.precos ?? ROLE_PRECOS_DEFAULT;
  const isAdmin = !gateEnabled || roles.includes(roleAdmin);
  // Preços: admin da ferramenta OU papel dedicado `precos`.
  const canPrecos = !gateEnabled || roles.includes(roleAdmin) || roles.includes(rolePrecos);

  return {
    isLoading,
    gateEnabled,
    canBase,
    canExterno,
    hasAny: canBase || canExterno || canRelatorios || canPrecos,
    verTodos,
    canRelatorios,
    isAdmin,
    canPrecos,
  };
};
